import { useEffect, useState } from 'react';
import { SelectItem } from '@/components/ui/select';
import { SelectField } from '@/components/dropdowns/SelectField/SelectField';
import { useFonts } from '@/context/FontProvider';
import type { FormProps } from '@builder4/shared/components/Form';
import { useInspectorNode } from '@/hooks/useInspectorNode';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { FORM_NODE_NAME } from '@/utils/constants';

const NodeFieldInputFontWeight = ({ className = 'w-full' }: { className?: string }) => {
  const { fontManager } = useFonts();
  const { id, actions, props: nodeProps, node } = useInspectorNode<Partial<FormProps>>();

  const { getBPProp, setBPProp } = useBreakpointAwareProp(node?.data.displayName || FORM_NODE_NAME);

  // Read props using breakpoint-aware getter
  const fieldInputFontFamily = getBPProp(nodeProps, 'fieldInputFontFamily');
  const fieldInputFontWeight = getBPProp(nodeProps, 'fieldInputFontWeight');

  const [weights, setWeights] = useState<
    Array<{ label: string; value: string; weight: string; style: string }>
  >([]);
  const [currentWeight, setCurrentWeight] = useState(String(fieldInputFontWeight || 500));

  useEffect(() => {
    setCurrentWeight(String(fieldInputFontWeight || 500));
  }, [fieldInputFontWeight]);

  useEffect(() => {
    if (fieldInputFontFamily && fontManager) {
      const availableWeights = fontManager.getAvailableWeights(fieldInputFontFamily);
      const nonItalicWeights = availableWeights.filter((w) => !/italic/i.test(w.value));
      setWeights(nonItalicWeights);
    }
  }, [fieldInputFontFamily, fontManager]);

  const handleWeightChange = (variant: string) => {
    const weight = weights.find((w) => w.value === variant);
    if (!weight) return;

    actions.setProp(id, (props: FormProps) => {
      setBPProp(props, 'fieldInputFontWeight', weight.weight, FORM_NODE_NAME);
    });
  };

  if (weights.length === 0) {
    return (
      <SelectField className={className} size="small" text="Weight" defaultValue="500">
        <SelectItem value="500">Loading...</SelectItem>
      </SelectField>
    );
  }

  return (
    <SelectField
      onValueChange={handleWeightChange}
      className={className}
      defaultValue={currentWeight}
      size="small"
      text="Weight"
      truncate={false}
    >
      {weights.map((weight) => (
        <SelectItem key={weight.value} value={weight.weight}>
          {weight.label}
        </SelectItem>
      ))}
    </SelectField>
  );
};

export default NodeFieldInputFontWeight;
